import React, { useEffect, useMemo, useState } from "react";
import { api, fmtDate, formatErr } from "../../api";
import { SectionTitle, EmptyState } from "../../components/Atoms";
import { Buildings, Plus, X, Trash, Pause, Play, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";

const PLAN_META = {
  free:       { label: "Free",       color: "#94A3B8" },
  pro:        { label: "Pro",        color: "#22D3EE" },
  enterprise: { label: "Enterprise", color: "#A3E635" },
};

function OrgFormModal({ initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || "");
  const [slug, setSlug] = useState(initial?.slug || "");
  const [type, setType] = useState(initial?.type || "brand");
  const [plan, setPlan] = useState(initial?.plan || "free");
  const [status, setStatus] = useState(initial?.status || "Active");
  // Owner-provisioning fields — only shown on CREATE. Optional but recommended;
  // without them the org exists but no one can log in as its owner.
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPassword, setOwnerPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim() || !slug.trim()) { toast.error("Name and slug are required"); return; }
    if (!isEdit) {
      // On create, require owner credentials so the tenant is usable immediately.
      if (!ownerName.trim() || !ownerEmail.trim() || !ownerPassword.trim()) {
        toast.error("Enter the owner's name, email and password so they can log in.");
        return;
      }
      if (ownerPassword.length < 6) { toast.error("Owner password must be at least 6 characters"); return; }
    }
    setBusy(true);
    try {
      const body = { name: name.trim(), slug: slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, "-"),
                     type, plan, status };
      if (!isEdit) {
        body.owner_name = ownerName.trim();
        body.owner_email = ownerEmail.trim().toLowerCase();
        body.owner_password = ownerPassword;
      }
      const res = isEdit
        ? await api.put(`/organizations/${initial.id}`, body)
        : await api.post(`/organizations`, body);
      toast.success(isEdit ? "Organization updated" : `Organization created — ${ownerEmail} can now log in.`);
      onSaved(res.data);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div className="glass w-full max-w-lg p-5 relative" onClick={e => e.stopPropagation()} data-testid="org-form-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-muted hover:text-white"><X size={18} /></button>
        <div className="text-white font-bold text-lg">{isEdit ? "Edit Organization" : "Create Organization"}</div>
        <div className="text-xs text-muted mt-1">
          {isEdit ? "Change type or lifecycle status." : "Create a new tenant + its owner login in one step."}
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted">Workspace name</label>
            <input className="input-base mt-2" value={name} onChange={e => setName(e.target.value)}
                   data-testid="org-form-name" placeholder="Studio Nova" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted">Slug</label>
            <input className="input-base mt-2" value={slug} onChange={e => setSlug(e.target.value)}
                   data-testid="org-form-slug" placeholder="studio-nova" disabled={isEdit} />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted">Type</label>
            <select className="input-base mt-2" value={type} onChange={e => setType(e.target.value)} data-testid="org-form-type">
              <option value="brand">Brand</option>
              <option value="agency">Agency</option>
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider font-bold text-muted">Status</label>
            <select className="input-base mt-2" value={status} onChange={e => setStatus(e.target.value)} data-testid="org-form-status">
              <option value="Active">Active</option>
              <option value="Suspended">Suspended</option>
            </select>
          </div>

          {!isEdit && (
            <>
              <div className="sm:col-span-2 pt-2 border-t border-white/5">
                <div className="text-white font-bold text-sm">Owner login</div>
                <div className="text-[11px] text-muted mt-1">Sets up the first user who can sign in to this workspace.</div>
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Owner name</label>
                <input className="input-base mt-2" value={ownerName} onChange={e => setOwnerName(e.target.value)}
                       data-testid="org-form-owner-name" placeholder="Aria Mehta" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Owner email</label>
                <input type="email" className="input-base mt-2" value={ownerEmail} onChange={e => setOwnerEmail(e.target.value)}
                       data-testid="org-form-owner-email" placeholder="aria@studionova.com" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-muted">Temporary password</label>
                <input type="text" className="input-base mt-2" value={ownerPassword} onChange={e => setOwnerPassword(e.target.value)}
                       data-testid="org-form-owner-password" placeholder="Min 6 chars — share with tenant" />
              </div>
            </>
          )}

          <div className="sm:col-span-2 text-[11px] text-muted glass-soft p-3 rounded-xl">
            All workspaces are on the <span className="text-white font-bold">Free (launch)</span> plan — unlimited projects &amp; creators. Paid packages ship in a later update.
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <button className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
          <button className="btn-primary text-sm" disabled={busy} onClick={save} data-testid="org-form-save">
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Create organization")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminOrganizationsPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/organizations");
      setRows(data || []);
    } catch (err) { toast.error(formatErr(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (r.name || "").toLowerCase().includes(q) || (r.slug || "").toLowerCase().includes(q));
  }, [rows, filter]);

  const toggleStatus = async (o) => {
    const next = o.status === "Active" ? "Suspended" : "Active";
    try {
      await api.put(`/organizations/${o.id}`, {
        name: o.name, slug: o.slug, type: o.type, plan: o.plan || "free", status: next,
      });
      toast.success(`Organization ${next.toLowerCase()}`);
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  const remove = async (o) => {
    if (o.slug === "default") { toast.error("Cannot delete Default Organization"); return; }
    if (!window.confirm(`Delete "${o.name}"? This purges ALL tenant data (creators, projects, submissions, payouts).`)) return;
    try {
      await api.delete(`/organizations/${o.id}`);
      toast.success("Organization deleted");
      await load();
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-organizations-page">
      <SectionTitle
        title="Organizations"
        subtitle="Every tenant in CrewHQ. Suspend or purge a workspace, tweak plan limits, or spin up a new one."
        action={
          <button className="btn-primary flex items-center gap-2" onClick={() => { setEditing(null); setShowForm(true); }}
                  data-testid="new-org-btn">
            <Plus size={14} weight="bold" /> New organization
          </button>
        }
      />

      <div className="flex items-center gap-3">
        <input className="input-base max-w-sm" placeholder="Search by name or slug" value={filter}
               onChange={e => setFilter(e.target.value)} data-testid="org-search" />
        <div className="text-xs text-muted">{filtered.length} total</div>
      </div>

      {loading ? (
        <div className="text-muted text-sm">Loading…</div>
      ) : filtered.length === 0 ? (
        <EmptyState title="No organizations" subtitle="Create your first tenant." icon={Buildings} />
      ) : (
        <div className="grid gap-2">
          {filtered.map(o => {
            const plan = PLAN_META[o.plan || "free"] || PLAN_META.free;
            const isSuspended = o.status === "Suspended";
            const isDefault = o.slug === "default";
            return (
              <div key={o.id} className="glass-soft p-4 flex flex-wrap items-center gap-3" data-testid={`org-row-${o.slug}`}>
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center text-white font-black">
                  {(o.name || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold truncate flex items-center gap-2">
                    {o.name} <span className="text-[10px] text-muted font-mono">/{o.slug}</span>
                    {isDefault && <span className="pill text-[10px]" style={{color:"#A3E635", background:"#A3E63522", borderColor:"#A3E63555"}}>Default</span>}
                  </div>
                  <div className="text-[11px] text-muted mt-0.5 flex items-center gap-3">
                    <span>Type: <span className="text-white">{o.type}</span></span>
                    <span className="pill text-[10px]" style={{color:"#A3E635", background:"#A3E63522", borderColor:"#A3E63555"}}>Free (launch)</span>
                    <span className="pill text-[10px]" style={{color: isSuspended ? "#EC4899" : "#A3E635",
                      background: isSuspended ? "#EC489922" : "#A3E63522",
                      borderColor: isSuspended ? "#EC489955" : "#A3E63555"}}>{o.status}</span>
                    <span>Created: <span className="text-white">{fmtDate(o.created_at)}</span></span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => { setEditing(o); setShowForm(true); }}
                          data-testid={`org-edit-${o.slug}`}>
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                  {!isDefault && (
                    <button className="btn-ghost text-xs flex items-center gap-1" onClick={() => toggleStatus(o)}
                            data-testid={`org-toggle-${o.slug}`}>
                      {isSuspended ? <Play size={12} weight="fill" /> : <Pause size={12} weight="fill" />}
                      {isSuspended ? "Activate" : "Suspend"}
                    </button>
                  )}
                  {!isDefault && (
                    <button className="btn-ghost text-xs flex items-center gap-1 text-[#EC4899]" onClick={() => remove(o)}
                            data-testid={`org-delete-${o.slug}`}>
                      <Trash size={12} weight="bold" /> Delete
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <OrgFormModal initial={editing} onClose={() => setShowForm(false)}
                      onSaved={async () => { setShowForm(false); await load(); }} />
      )}
    </div>
  );
}
